import { useForm } from '@inertiajs/react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toastFlash } from '@/lib/flash-toast';
import type { MentorAdmin } from '@/types';
import { MentorPictureUpload } from './MentorPictureUpload';

interface EditMentorDialogProps {
  eventSlug: string;
  mentor: MentorAdmin;
  onClose: () => void;
}

type AvailabilitySlot = {
  day: string;
  startTime: string;
  endTime: string;
  tentative: boolean;
};

// Mounted only while a mentor is being edited and keyed on its id, which is what the
// `form.reset(...)` inside the Next dialog's effect achieved.
export function EditMentorDialog({
  eventSlug,
  mentor,
  onClose,
}: EditMentorDialogProps) {
  const form = useForm({
    fullName: mentor.fullName,
    github: mentor.github,
    linkedin: mentor.linkedin || '',
    pictureUrl: mentor.pictureUrl || '',
    companyTitle: mentor.companyTitle || '',
    availability: (mentor.availability || []).map((slot) => ({
      day: slot.day,
      startTime: slot.startTime,
      endTime: slot.endTime,
      tentative: slot.tentative ?? false,
    })) as AvailabilitySlot[],
  });
  const { data, setData, processing } = form;
  const errors = form.errors as unknown as Record<string, string | undefined>;

  const updateSlot = (index: number, patch: Partial<AvailabilitySlot>) =>
    setData(
      'availability',
      data.availability.map((slot, i) =>
        i === index ? { ...slot, ...patch } : slot,
      ),
    );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.patch(`/admin/${eventSlug}/mentors/${mentor.id}`, {
      preserveScroll: true,
      onSuccess: (page) => {
        toastFlash(page, 'Mentor updated');
        onClose();
      },
      onError: () =>
        toast({ type: 'error', description: 'Failed to update mentor' }),
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Mentor</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="edit-mentor-full-name">Full Name</Label>
            <Input
              id="edit-mentor-full-name"
              placeholder="John Doe"
              value={data.fullName}
              onChange={(e) => setData('fullName', e.target.value)}
            />
            {errors.fullName && (
              <p className="text-destructive text-sm">{errors.fullName}</p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="edit-mentor-github">GitHub URL</Label>
            <Input
              id="edit-mentor-github"
              placeholder="https://github.com/username"
              value={data.github}
              onChange={(e) => setData('github', e.target.value)}
            />
            {errors.github && (
              <p className="text-destructive text-sm">{errors.github}</p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="edit-mentor-linkedin">
              LinkedIn URL (optional)
            </Label>
            <Input
              id="edit-mentor-linkedin"
              placeholder="https://linkedin.com/in/username"
              value={data.linkedin}
              onChange={(e) => setData('linkedin', e.target.value)}
            />
            {errors.linkedin && (
              <p className="text-destructive text-sm">{errors.linkedin}</p>
            )}
          </div>
          <div className="grid gap-2">
            <Label>Picture</Label>
            <MentorPictureUpload
              mentorId={mentor.id}
              eventSlug={eventSlug}
              currentPictureUrl={data.pictureUrl || null}
              mentorName={mentor.fullName}
              onUploaded={(url) => setData('pictureUrl', url)}
            />
            {errors.pictureUrl && (
              <p className="text-destructive text-sm">{errors.pictureUrl}</p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="edit-mentor-company-title">
              Company / Title (optional)
            </Label>
            <Input
              id="edit-mentor-company-title"
              placeholder="Software Engineer at Acme Inc"
              value={data.companyTitle}
              onChange={(e) => setData('companyTitle', e.target.value)}
            />
            {errors.companyTitle && (
              <p className="text-destructive text-sm">{errors.companyTitle}</p>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Availability Schedule</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setData('availability', [
                    ...data.availability,
                    {
                      day: 'friday',
                      startTime: '18:00',
                      endTime: '23:00',
                      tentative: false,
                    },
                  ])
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Slot
              </Button>
            </div>

            {data.availability.length === 0 && (
              <p className="text-muted-foreground text-sm">
                No availability slots added yet.
              </p>
            )}

            {data.availability.map((slot, index) => (
              <div
                // The slots have no id of their own; their position is their identity,
                // exactly as in the Next field array.
                key={`${index}-${slot.day}`}
                className="flex gap-2 rounded-md border p-3"
              >
                <div className="flex-1">
                  <Select
                    value={slot.day}
                    onValueChange={(day) => updateSlot(index, { day })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Day" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="friday">Friday</SelectItem>
                      <SelectItem value="saturday">Saturday</SelectItem>
                      <SelectItem value="sunday">Sunday</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1">
                  <Input
                    placeholder="18:00"
                    type="time"
                    value={slot.startTime}
                    onChange={(e) =>
                      updateSlot(index, { startTime: e.target.value })
                    }
                  />
                </div>

                <div className="flex-1">
                  <Input
                    placeholder="23:00"
                    type="time"
                    value={slot.endTime}
                    onChange={(e) =>
                      updateSlot(index, { endTime: e.target.value })
                    }
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`edit-mentor-tentative-${index}`}
                    checked={slot.tentative}
                    onCheckedChange={(checked) =>
                      updateSlot(index, { tentative: checked === true })
                    }
                  />
                  <Label
                    htmlFor={`edit-mentor-tentative-${index}`}
                    className="!mt-0 text-sm"
                  >
                    TBC
                  </Label>
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() =>
                    setData(
                      'availability',
                      data.availability.filter((_, i) => i !== index),
                    )
                  }
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
            {errors.availability && (
              <p className="text-destructive text-sm">{errors.availability}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
